import { useState, useMemo, useEffect } from "react";
import { startOfWeek, endOfWeek, startOfDay, endOfDay, format } from "date-fns";
import { Views, type View } from "react-big-calendar";
import TimesheetCalendar, {
  type TimesheetEvent,
} from "../components/timesheets/TimesheetCalendar";
import { useTeamworkUsers, useTimesheets } from "../hooks/useTimesheets";
import styles from "./Timesheets.module.css";

type Mode = "single" | "multi";

export default function Timesheets() {
  const { users, loading: usersLoading } = useTeamworkUsers();
  const [mode, setMode] = useState<Mode>("multi");
  const [selectedSingle, setSelectedSingle] = useState<string>("");
  const [selectedMulti, setSelectedMulti] = useState<string[]>([]);
  const [autoSelectedAll, setAutoSelectedAll] = useState(false);
  const [view, setView] = useState<View>(Views.DAY);

  useEffect(() => {
    if (!autoSelectedAll && users.length > 0) {
      setSelectedMulti(users.map((u) => u.id));
      setAutoSelectedAll(true);
    }
  }, [users, autoSelectedAll]);
  const [date, setDate] = useState<Date>(new Date());
  const [popover, setPopover] = useState<{
    event: TimesheetEvent;
    top: number;
    left: number;
  } | null>(null);

  const userIds =
    mode === "single"
      ? selectedSingle
        ? [selectedSingle]
        : []
      : selectedMulti;

  const { fromDate, toDate } = useMemo(() => {
    if (view === Views.WEEK) {
      return {
        fromDate: startOfWeek(date, { weekStartsOn: 1 }),
        toDate: endOfWeek(date, { weekStartsOn: 1 }),
      };
    }
    return { fromDate: startOfDay(date), toDate: endOfDay(date) };
  }, [view, date]);

  const { entries, loading, error } = useTimesheets({
    userIds,
    fromDate,
    toDate,
  });

  const handleModeChange = (next: Mode) => {
    setMode(next);
    setView(next === "multi" ? Views.DAY : Views.WEEK);
    setPopover(null);
  };

  const resources =
    mode === "multi"
      ? users.filter((u) => selectedMulti.includes(u.id))
      : undefined;

  const totals = useMemo(() => {
    let billable = 0;
    let nonBillable = 0;
    for (const e of entries) {
      const mins =
        (parseInt(e.hours, 10) || 0) * 60 + (parseInt(e.minutes, 10) || 0);
      if (e.isbillable === "1") billable += mins;
      else nonBillable += mins;
    }
    return { billable, nonBillable };
  }, [entries]);

  const formatMins = (m: number) => {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h}h ${mm}m`;
  };

  return (
    <div className={styles.page} onClick={() => setPopover(null)}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Timesheets</h1>
          <p className={styles.subtitle}>
            View any team member's Teamwork time logs in a weekly grid.
          </p>
        </div>
      </header>

      <div className={styles.controls}>
        <div className={styles.modeToggle}>
          <button
            className={mode === "single" ? styles.toggleActive : styles.toggle}
            onClick={() => handleModeChange("single")}
          >
            Single user (week)
          </button>
          <button
            className={mode === "multi" ? styles.toggleActive : styles.toggle}
            onClick={() => handleModeChange("multi")}
          >
            Multi user (day)
          </button>
        </div>

        {mode === "single" ? (
          <select
            className={styles.select}
            value={selectedSingle}
            onChange={(e) => setSelectedSingle(e.target.value)}
            disabled={usersLoading}
          >
            <option value="">Select a user</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u["first-name"]} {u["last-name"]}
              </option>
            ))}
          </select>
        ) : (
          <div className={styles.multiPicker}>
            {users.map((u) => {
              const checked = selectedMulti.includes(u.id);
              return (
                <label key={u.id} className={styles.checkLabel}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      setSelectedMulti((prev) =>
                        prev.includes(u.id)
                          ? prev.filter((x) => x !== u.id)
                          : [...prev, u.id],
                      );
                    }}
                  />
                  {u["first-name"]} {u["last-name"]}
                </label>
              );
            })}
          </div>
        )}

        <div className={styles.totals}>
          <span>
            <span className={styles.dotBillable} /> Billable{" "}
            {formatMins(totals.billable)}
          </span>
          <span>
            <span className={styles.dotNonBillable} /> Non-billable{" "}
            {formatMins(totals.nonBillable)}
          </span>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {loading && <div className={styles.loading}>Loading time entries...</div>}

      <div className={styles.calendarWrap}>
        <TimesheetCalendar
          entries={entries}
          view={view}
          date={date}
          onView={setView}
          onNavigate={setDate}
          resources={resources}
          onSelectEvent={(event, target) => {
            const rect = target.getBoundingClientRect();
            setPopover({
              event,
              top: rect.bottom + window.scrollY + 4,
              left: rect.left + window.scrollX,
            });
          }}
        />
      </div>

      {popover && (
        <div
          className={styles.popover}
          style={{ top: popover.top, left: popover.left }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.popoverTitle}>{popover.event.title}</div>
          <div className={styles.popoverRow}>
            <span className={styles.popoverLabel}>Project</span>
            <span>{popover.event.raw["project-name"]}</span>
          </div>
          {popover.event.raw["todo-item-name"] && (
            <div className={styles.popoverRow}>
              <span className={styles.popoverLabel}>Task</span>
              <span>{popover.event.raw["todo-item-name"]}</span>
            </div>
          )}
          <div className={styles.popoverRow}>
            <span className={styles.popoverLabel}>Person</span>
            <span>
              {popover.event.raw["person-first-name"]}{" "}
              {popover.event.raw["person-last-name"]}
            </span>
          </div>
          <div className={styles.popoverRow}>
            <span className={styles.popoverLabel}>Time</span>
            <span>
              {format(popover.event.start, "EEE d MMM, HH:mm")} –{" "}
              {format(popover.event.end, "HH:mm")}
            </span>
          </div>
          <div className={styles.popoverRow}>
            <span className={styles.popoverLabel}>Duration</span>
            <span>
              {popover.event.raw.hours}h {popover.event.raw.minutes}m
            </span>
          </div>
          <div className={styles.popoverRow}>
            <span className={styles.popoverLabel}>Billable</span>
            <span>{popover.event.raw.isbillable === "1" ? "Yes" : "No"}</span>
          </div>
          <div className={styles.popoverActions}>
            {popover.event.raw["todo-item-id"] && (
              <a
                className={styles.popoverButton}
                href={`https://teamwork.kinghenry.au/app/tasks/${popover.event.raw["todo-item-id"]}`}
                target="_blank"
                rel="noreferrer"
              >
                Open task
              </a>
            )}
            <a
              className={styles.popoverButton}
              href={`https://teamwork.kinghenry.au/app/projects/${popover.event.raw["project-id"]}/time/all/log`}
              target="_blank"
              rel="noreferrer"
            >
              Open in Teamwork
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
